import { FocusOrigin } from '@angular/cdk/a11y';
import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { CDK_MENU, CdkMenu, CdkMenuGroup, CdkMenuItem, PARENT_OR_NEW_INLINE_MENU_STACK_PROVIDER } from '@angular/cdk/menu';
import { Component, forwardRef } from '@angular/core';
import { asapScheduler, observeOn, takeUntil } from 'rxjs';

/** Action menu only. Forms and draft filters belong in a popover. */
@Component({
  selector: 'br-menu',
  standalone: true,
  providers: [
    { provide: CdkMenu, useExisting: forwardRef(() => BrMenuComponent) },
    { provide: CdkMenuGroup, useExisting: forwardRef(() => BrMenuComponent) },
    { provide: CDK_MENU, useExisting: forwardRef(() => BrMenuComponent) },
    PARENT_OR_NEW_INLINE_MENU_STACK_PROVIDER('vertical'),
  ],
  host: { '(keydown)': 'stopHandledEscape($event)' },
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.css'],
})
export class BrMenuComponent extends CdkMenu {
  override ngAfterContentInit(): void {
    super.ngAfterContentInit();
    // Content queries update during change detection; focus after that check.
    this.items.changes.pipe(observeOn(asapScheduler), takeUntil(this.destroyed)).subscribe(() => {
      const active = this.keyManager.activeItem;
      if (active && !this.items.toArray().includes(active)) this.focusItemReplacingRemoved();
    });
  }

  /**
   * Material 17.3.10 deletion-focus parity (`_directDescendantItems.changes` in menu.mjs).
   * CDK's key manager retains the removed item's index; clamp it into the remaining list and
   * focus that item, or the next enabled one with wrap when the candidate is disabled.
   */
  private focusItemReplacingRemoved(): void {
    const manager = this.keyManager;
    const items = this.items.toArray();
    const index = Math.max(0, Math.min(items.length - 1, manager.activeItemIndex ?? 0));
    if (items[index] && !items[index].disabled) manager.setActiveItem(index);
    else manager.setNextItemActive();
    this.focusPanelIfEmpty();
  }

  /** Keep CDK's existing key manager in sync with DOM/programmatic focus. */
  syncFocusedItem(item: CdkMenuItem): void {
    this.keyManager?.updateActiveItem(item);
  }

  override focusFirstItem(origin: FocusOrigin = 'program'): void {
    super.focusFirstItem(origin);
    this.focusPanelIfEmpty();
    this.nativeElement.scrollTop = 0;
  }

  override focusLastItem(origin: FocusOrigin = 'program'): void {
    super.focusLastItem(origin);
    this.focusPanelIfEmpty();
  }

  private focusPanelIfEmpty(): void {
    if (!this.items.some(item => !item.disabled)) {
      this.nativeElement.tabIndex = -1;
      this.nativeElement.focus();
    }
  }

  stopHandledEscape(event: KeyboardEvent): void {
    // CDK closes this menu. Keep the same Escape from reaching an enclosing dialog.
    if (event.keyCode === ESCAPE && !hasModifierKey(event)) event.stopPropagation();
  }
}
